import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const provinceItems = [
  { label: '高校覆盖', icon: 'ri-building-3-line', desc: '参与院校范围' },
  { label: '课程覆盖', icon: 'ri-file-list-3-line', desc: '课程数量与类型' },
  { label: '学校类型对比', icon: 'ri-bar-chart-line', desc: '综合/理工/师范等' },
  { label: '课程主题趋势', icon: 'ri-timer-line', desc: '主题变化方向' },
  { label: '质量维度分布', icon: 'ri-radar-line', desc: '各维度得分分布' },
  { label: '优秀示范区域', icon: 'ri-medal-line', desc: '标杆区域' },
  { label: '重点支持区域', icon: 'ri-focus-2-line', desc: '需加强区域' },
];

export default function P73ProvinceMap({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />一省一图
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            看区域趋势与<span className="text-primary-600">资源配置方向</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            区域治理看趋势和支持方向，不把不同学校、不同课程压缩为单一排名
          </p>
        </div>

        {/* Filter indicator */}
        <div className={`max-w-lg mx-auto mb-3 transition-all duration-1000 delay-250 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="flex items-center justify-center gap-3">
            <span className="px-3 py-1.5 rounded-full text-xs font-body font-semibold bg-primary-100/60 text-primary-700 border border-primary-200/40">按课程查看</span>
            <span className="text-xs text-foreground-400">|</span>
            <span className="px-3 py-1.5 rounded-full text-xs font-body font-semibold bg-gold-100/80 text-gold-700 border border-gold-300/40">跨课程共同维度</span>
          </div>
        </div>

        <div className={`max-w-5xl mx-auto transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 md:gap-4">
            {provinceItems.map((item, i) => (
              <div
                key={item.label}
                className="p-4 glass-liquid rounded-xl text-center"
                style={{ transitionDelay: `${300 + i * 80}ms` }}
              >
                <div className="w-10 h-10 mx-auto mb-3 rounded-xl bg-primary-100/60 border border-primary-200/40 flex items-center justify-center">
                  <i className={`${item.icon} text-lg text-primary-600`} />
                </div>
                <h4 className="text-sm font-body font-bold text-foreground-800 mb-1">{item.label}</h4>
                <p className="text-xs text-foreground-500 font-body">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center mt-4 transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              省域视图的价值，不是给学校做简单排名，而是识别哪些地区课程质量较为稳定、哪些学校已形成可推广经验、哪些课程主题需要<strong className="text-gold-700">加强资源建设</strong>、哪些教师群体需要<strong className="text-gold-700">专题培训</strong>。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}